import React, { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { Tab } from '@headlessui/react'
import {
  BanknotesIcon,
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  CreditCardIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { api, monetizationAPI } from '../services/api'
import { useAuthStore } from '../store/authStore'
import { Payout, Subscription, Transaction } from '../types'
import ConfirmDialog from '../components/ConfirmDialog'
import Skeleton from '../components/Skeleton'
import SubscriptionsList from '../components/monetization/SubscriptionsList'
import TransactionHistory from '../components/monetization/TransactionHistory'
import PaymentMethodsPanel from '../components/monetization/PaymentMethodsPanel'
import UnlockedContent from '../components/drip/UnlockedContent'
import { displayName, formatCents, subscriptionSummary } from '../components/monetization/subscriptionsView'

const detail = (error: any, fallback: string): string => error?.response?.data?.detail || fallback

const TABS = [
  { name: 'My Subscriptions', short: 'Subscriptions', icon: UserGroupIcon },
  { name: 'Transactions', short: 'Transactions', icon: ClipboardDocumentListIcon },
  { name: 'Payment Methods', short: 'Cards', icon: CreditCardIcon },
]
const PAYMENT_METHODS_TAB = 2

const Stat: React.FC<{ icon: typeof BanknotesIcon; label: string; children: React.ReactNode; hint?: string }> = ({
  icon: Icon,
  label,
  children,
  hint,
}) => (
  <div className="min-w-0 rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
    <div className="flex items-center gap-2 text-gray-500">
      <span className="hidden h-7 w-7 items-center justify-center rounded-lg bg-accent/10 sm:flex">
        <Icon className="h-4 w-4 text-accent" aria-hidden="true" />
      </span>
      <span className="truncate text-[11px] font-medium uppercase tracking-wide sm:text-xs">{label}</span>
    </div>
    <div className="mt-1 text-lg font-bold tabular-nums text-gray-900 sm:mt-2 sm:text-2xl">{children}</div>
    {hint && <p className="mt-0.5 text-[11px] leading-snug text-gray-500 sm:text-xs">{hint}</p>}
  </div>
)

/** Only what the subscriptions say; a stat with nothing behind it isn't shown. */
const SummaryStrip: React.FC<{ subscriptions: Subscription[]; isLoading: boolean }> = ({
  subscriptions,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-[repeat(auto-fit,minmax(6.5rem,1fr))] gap-2 sm:gap-3" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-20 rounded-xl sm:h-[6.5rem]" />
        ))}
      </div>
    )
  }
  const summary = subscriptionSummary(subscriptions)
  if (summary.activeCount === 0 && !summary.nextCharge) return null
  return (
    <div
      className="grid grid-cols-[repeat(auto-fit,minmax(6.5rem,1fr))] gap-2 sm:gap-3 motion-safe:animate-fade-in"
      data-testid="subscription-summary"
    >
      {summary.activeCount > 0 && (
        <Stat icon={UserGroupIcon} label="Active">
          {summary.activeCount}
        </Stat>
      )}
      {summary.nextCharge && (
        <Stat
          icon={CalendarDaysIcon}
          label="Next charge"
          hint={format(new Date(summary.nextCharge.date), 'MMM d, yyyy')}
        >
          {formatCents(summary.nextCharge.amountCents)}
        </Stat>
      )}
      {summary.monthlyCents != null && (
        <Stat
          icon={BanknotesIcon}
          label="Monthly total"
          hint={summary.includesYearly ? 'Yearly plans ÷ 12' : undefined}
        >
          {formatCents(summary.monthlyCents)}
        </Stat>
      )}
    </div>
  )
}

const Subscriptions: React.FC = () => {
  const currentUserId = useAuthStore((state) => state.user?.id)
  const [tabIndex, setTabIndex] = useState(0)
  const [addCardOnOpen, setAddCardOnOpen] = useState(false)

  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [subsLoading, setSubsLoading] = useState(true)
  const [subsError, setSubsError] = useState<string | null>(null)

  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [payouts, setPayouts] = useState<Payout[]>([])
  const [txLoading, setTxLoading] = useState(true)
  const [txError, setTxError] = useState<string | null>(null)

  const [cancelling, setCancelling] = useState<Subscription | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [isOpeningPortal, setIsOpeningPortal] = useState(false)

  const loadSubscriptions = useCallback(async () => {
    setSubsLoading(true)
    setSubsError(null)
    try {
      // Not only active: past-due and cancelling ones belong on the page too.
      setSubscriptions(await monetizationAPI.getSubscriptions('all'))
    } catch (error: any) {
      setSubsError(detail(error, 'Couldn’t load your subscriptions.'))
    } finally {
      setSubsLoading(false)
    }
  }, [])

  const loadTransactions = useCallback(async () => {
    setTxLoading(true)
    setTxError(null)
    try {
      const [txs, payoutList] = await Promise.all([
        api.get<Transaction[]>('/api/monetization/transactions').then((r) => r.data),
        // Payouts are a creator's; anyone else simply has none.
        monetizationAPI.getPayouts().catch(() => [] as Payout[]),
      ])
      setTransactions(txs)
      setPayouts(payoutList)
    } catch (error: any) {
      setTxError(detail(error, 'Couldn’t load your transactions.'))
    } finally {
      setTxLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSubscriptions()
    loadTransactions()
  }, [loadSubscriptions, loadTransactions])

  const confirmCancel = async () => {
    if (!cancelling) return
    setIsCancelling(true)
    try {
      await monetizationAPI.cancelSubscription(cancelling.id)
      toast.success('Subscription cancelled. You keep access until the period ends.')
      setCancelling(null)
      loadSubscriptions()
    } catch (error: any) {
      toast.error(detail(error, 'Failed to cancel subscription'))
    } finally {
      setIsCancelling(false)
    }
  }

  const handleReactivate = async (subscription: Subscription) => {
    try {
      await monetizationAPI.reactivateSubscription(subscription.id)
      toast.success('Subscription resumed')
      loadSubscriptions()
    } catch (error: any) {
      toast.error(detail(error, 'Failed to resume subscription'))
    }
  }

  // Stripe's Customer Portal: cards, invoices and receipts, hosted by Stripe.
  const openBillingPortal = async () => {
    setIsOpeningPortal(true)
    try {
      const { data } = await api.post<{ url: string }>('/api/monetization/billing-portal')
      window.location.href = data.url
    } catch (error: any) {
      toast.error(detail(error, 'Could not open the billing portal'))
      setIsOpeningPortal(false)
    }
  }

  const goUpdateCard = () => {
    setAddCardOnOpen(true)
    setTabIndex(PAYMENT_METHODS_TAB)
  }

  const cancellingName = cancelling?.creator ? displayName(cancelling.creator) : 'this creator'

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
        <header>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Subscriptions</h1>
          <p className="mt-1 text-gray-600">The creators you subscribe to, what you&rsquo;ve paid, and your cards.</p>
        </header>

        {!subsError && <SummaryStrip subscriptions={subscriptions} isLoading={subsLoading} />}

        <Tab.Group
          selectedIndex={tabIndex}
          onChange={(index) => {
            setTabIndex(index)
            if (index !== PAYMENT_METHODS_TAB) setAddCardOnOpen(false)
          }}
        >
          <Tab.List className="flex w-full gap-1 rounded-xl border border-gray-200 bg-white p-1 shadow-sm sm:w-auto sm:inline-flex">
            {TABS.map((tab) => (
              <Tab
                key={tab.name}
                className={({ selected }) =>
                  `flex flex-1 flex-col items-center gap-0.5 rounded-lg px-2 py-2 text-xs font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent sm:flex-none sm:flex-row sm:gap-2 sm:px-4 sm:text-sm ${
                    selected
                      ? 'bg-accent text-terminal-bg shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`
                }
              >
                <tab.icon className="h-5 w-5" aria-hidden="true" />
                <span className="sm:hidden">{tab.short}</span>
                <span className="hidden sm:inline">{tab.name}</span>
              </Tab>
            ))}
          </Tab.List>

          <Tab.Panels className="mt-5">
            <Tab.Panel className="focus:outline-none motion-safe:animate-fade-in">
              <SubscriptionsList
                subscriptions={subscriptions}
                isLoading={subsLoading}
                error={subsError}
                onRetry={loadSubscriptions}
                onCancel={setCancelling}
                onReactivate={handleReactivate}
                onManage={openBillingPortal}
                onUpdateCard={goUpdateCard}
              />
              {!subsLoading && <UnlockedContent subscriptions={subscriptions} />}
            </Tab.Panel>

            <Tab.Panel className="focus:outline-none motion-safe:animate-fade-in">
              <TransactionHistory
                transactions={transactions}
                payouts={payouts}
                currentUserId={currentUserId}
                counterpartyLabel="Creator"
                isLoading={txLoading}
                error={txError}
                onRetry={loadTransactions}
              />
            </Tab.Panel>

            <Tab.Panel className="focus:outline-none motion-safe:animate-fade-in">
              <PaymentMethodsPanel
                startAdding={addCardOnOpen}
                onOpenPortal={openBillingPortal}
                isOpeningPortal={isOpeningPortal}
              />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>

      <ConfirmDialog
        isOpen={cancelling != null}
        title="Cancel subscription?"
        message={`Your subscription to ${cancellingName} stays active until the end of the current billing period, and you won't be charged again.`}
        confirmLabel="Cancel subscription"
        cancelLabel="Keep it"
        danger
        confirming={isCancelling}
        onConfirm={confirmCancel}
        onCancel={() => setCancelling(null)}
      />
    </div>
  )
}

export default Subscriptions
